import { ChevronDown, ChevronRight, Star } from 'lucide-react';
import { ForecastModel, BaseForecastModelWithAvailability } from '@app/common/static-data/models';
import { useTheme } from '@app/theme/ThemeContext';
import { ModelCard } from './ModelCard';
import { getModelColor } from '../utils/colorUtils';

interface ModelSectionProps {
  selectedModels: ForecastModel[];
  onModelsChange: (models: ForecastModel[]) => void;
  availableModels: BaseForecastModelWithAvailability[];
  favoriteModels: Set<string>;
  onToggleFavorite: (modelId: string) => void;
  loading: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  customModelColors: Record<string, string>;
  onModelColorChange: (modelId: string, color: string) => void;
  availableInitTimes?: Record<string, string[]>;
  loadingInitTimes?: Record<string, boolean>;
  openInitTimeSelectors?: Record<string, boolean>;
  selectedInitTimes?: Record<string, string>;
  onInitTimeChange?: (modelId: string, initTime: string) => void;
  onInitTimeSelectorToggle?: (modelId: string) => void;
}

export function ModelSection({
  selectedModels,
  onModelsChange,
  availableModels,
  favoriteModels,
  onToggleFavorite,
  loading,
  expanded = false,
  onExpandedChange,
  customModelColors,
  onModelColorChange,
  availableInitTimes = {},
  loadingInitTimes = {},
  openInitTimeSelectors = {},
  selectedInitTimes = {},
  onInitTimeChange,
  onInitTimeSelectorToggle,
}: ModelSectionProps) {
  const { theme } = useTheme();

  // Get favorite models
  const favoriteModelsList = availableModels.filter((m) => favoriteModels.has(m.id));

  // Categorize non-favorite models
  const juaModels = availableModels.filter(
    (m) => m.provider === 'jua' && !favoriteModels.has(m.id),
  );
  const aiModels = availableModels.filter(
    (m) =>
      m.provider === 'external' &&
      (m.id.includes('aifs') || m.id.includes('graphcast') || m.id.includes('aurora')) &&
      !favoriteModels.has(m.id),
  );
  const numericalModels = availableModels.filter(
    (m) =>
      m.provider === 'external' &&
      !aiModels.some((ai) => ai.id === m.id) &&
      !favoriteModels.has(m.id),
  );

  const toggleModelSelection = (modelId: ForecastModel) => {
    if (selectedModels.includes(modelId)) {
      onModelsChange(selectedModels.filter((id) => id !== modelId));
    } else {
      onModelsChange([...selectedModels, modelId]);
    }
  };

  const FavoriteModelsGroup = () => {
    return (
      <div>
        <h5 className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
          Favorites
        </h5>
        {favoriteModelsList.length === 0 ? (
          <div className="text-center py-3 text-gray-500 dark:text-gray-400 text-sm">
            Select favorite models to see them here
          </div>
        ) : (
          <div className="space-y-2">
            {favoriteModelsList.map((model, index) => (
              <div key={model.id} className={`model-card-${model.id}`}>
                <ModelCard
                  model={model}
                  isSelected={selectedModels.includes(model.id as ForecastModel)}
                  isFavorite={true}
                  onToggle={() => toggleModelSelection(model.id as ForecastModel)}
                  onToggleFavorite={() => onToggleFavorite(model.id)}
                  disabled={loading}
                  modelColor={
                    customModelColors[model.id]
                      ? customModelColors[model.id]
                      : getModelColor(model.id, index, theme)
                  }
                  onColorChange={onModelColorChange}
                  availableInitTimes={availableInitTimes[model.id]}
                  loadingInitTimes={loadingInitTimes}
                  openInitTimeSelectors={openInitTimeSelectors}
                  selectedInitTime={selectedInitTimes[model.id] || 'live'}
                  onInitTimeChange={onInitTimeChange}
                  onInitTimeSelectorToggle={onInitTimeSelectorToggle}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const ModelGroup = ({
    title,
    models,
    icon,
  }: {
    title: string;
    models: BaseForecastModelWithAvailability[];
    icon: React.ReactNode;
  }) => {
    if (models.length === 0) return null;

    // Sort models alphabetically since favorites are now separate
    const sortedModels = models.sort((a, b) => a.label.localeCompare(b.label));

    return (
      <div>
        <h5 className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
          {icon && icon}
          {title}
        </h5>
        <div className="space-y-2">
          {sortedModels.map((model, index) => (
            <div key={model.id} className={`model-card-${model.id}`}>
              <ModelCard
                model={model}
                isSelected={selectedModels.includes(model.id as ForecastModel)}
                isFavorite={false}
                onToggle={() => toggleModelSelection(model.id as ForecastModel)}
                onToggleFavorite={() => onToggleFavorite(model.id)}
                disabled={loading}
                modelColor={
                  customModelColors[model.id]
                    ? customModelColors[model.id]
                    : getModelColor(model.id, index, theme)
                }
                onColorChange={onModelColorChange}
                availableInitTimes={availableInitTimes[model.id]}
                loadingInitTimes={loadingInitTimes}
                openInitTimeSelectors={openInitTimeSelectors}
                selectedInitTime={selectedInitTimes[model.id] || 'live'}
                onInitTimeChange={onInitTimeChange}
                onInitTimeSelectorToggle={onInitTimeSelectorToggle}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const favoriteCount = favoriteModelsList.length;
  const selectedCount = selectedModels.length;

  return (
    <div>
      <button
        onClick={() => onExpandedChange && onExpandedChange(!expanded)}
        className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-500" />
          )}
          <div className="text-left">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">Weather Models</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {selectedCount > 0 ? `${selectedCount} selected` : 'Select models'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          {favoriteCount > 0 && (
            <span className="flex items-center gap-1">
              <Star className="h-3 w-3 text-pink-500 fill-current" />
              {favoriteCount}
            </span>
          )}
          <span>{availableModels.length} total</span>
        </div>
      </button>

      {expanded && (
        <div className="mt-2 space-y-4 pl-7">
          <FavoriteModelsGroup />
          <ModelGroup title="Jua Models" models={juaModels} icon={null} />
          <ModelGroup title="Third party AI models" models={aiModels} icon={null} />
          <ModelGroup title="Numerical Models" models={numericalModels} icon={null} />
        </div>
      )}
    </div>
  );
}
